import { useState, useEffect, useCallback } from 'react';
import { menuApi } from '../api/menuApi';
import { useAuth } from '../contexts/AuthContext';

type UserPermission = {
  functionCode: string;
  actions: string[];
};

export const useFunctionPermissions = () => {
  const { isSuperAdmin, isOperator, isAuthenticated, loading: authLoading } = useAuth();
  const [permissions, setPermissions] = useState<UserPermission[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      setPermissions([]);
      setLoading(false);
      return;
    }

    if (isSuperAdmin || isOperator) {
      setPermissions([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    menuApi
      .getUserPermissions()
      .then((response) => {
        if (!cancelled) {
          setPermissions(response.permissions || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPermissions([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, isSuperAdmin, isOperator]);

  const can = useCallback(
    (functionCode: string, action: string): boolean => {
      if (isSuperAdmin || isOperator) {
        return true;
      }

      const permission = permissions.find((item) => item.functionCode === functionCode);
      return permission?.actions.includes(action) ?? false;
    },
    [isSuperAdmin, isOperator, permissions],
  );

  return {
    can,
    loading: loading || authLoading,
    permissions,
  };
};
